import { cn } from "@/lib/utils";
import { ItemPicture } from "./item-picture";
import { ItemPlaceholderMark } from "./item-placeholder-mark";
import {
  ITEM_IMAGE_BOX_SIZES,
  ITEM_PLACEHOLDER_ILLUSTRATION,
  type ItemImageBoxSize,
} from "./item-image-box-sizes";

// The donor's shared image box, and the shared empty box that stands in when
// there is no image — `ItemImageBox` (`timmy/src/domains/items/components/
// item-image-box/item-image-box.tsx`) and `ItemEmptyImageBox`
// (`timmy/src/components/assets/ItemEmptyImageBox.tsx`).
//
// **Here rather than in the feature folder because they are shared in the
// source**, which is what decides it (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components"). Between them the
// two donor files have nine call sites across the items domain and the shared
// asset tree — the catalog's three views, the variant tile strip, the Add
// Variant dialog, the item search island's card, and `ImagePreview`. The first
// slice to need one ports it globally; reproducing its shape as local classes
// is what makes the second screen drift.
//
// The full five-rung size map is carried over even though this slice calls two
// of the rungs, because that is the shape being shared: the variant tiles
// (#254) want `xsmall`, and a details page wants `large`. A rung invented at a
// call site is the drift this file exists to stop.

/**
 * The box itself, without the media — `boxStyles` (`item-image-box.tsx:35-46`).
 *
 * `display` and `padding` are deliberately **not** here: `ItemEmptyImageBox`
 * disagrees with `ItemImageBox` on both, and layering one over the other does
 * not work. Tailwind emits `.p-px` after `.p-[24px]` and `.inline-flex` after
 * `.flex`, so at equal specificity the *later rule* wins regardless of the order
 * the class string is written in. Each box composes its own pair instead, so
 * there is nothing for the cascade to decide.
 */
function boxShape(
  geometry: string | undefined,
  bordered: boolean | "light" | "primary",
  className?: string,
) {
  return cn(
    // 4px radius — the donor's `borderRadius: 1` against MUI's default
    // `shape.borderRadius`, which the donor theme never overrides. Not
    // `rounded-sm`, which resolves to 6px in this app.
    "shrink-0 items-center overflow-hidden rounded-[4px]",
    // How big: a rung's square, `w-full` where the caller states its own
    // height, or nothing at all where an inline style supplies both. It is the
    // one thing that differs between the four boxes below, which is why they
    // can all come through here — three hand-copied spellings of the radius and
    // the border is three places to edit when either changes.
    geometry,
    // `grey.300` — MUI's default, never overridden by the donor theme. Not
    // `--border`: that is #e5e5e5 and inverts under a dark mode the donor does
    // not have.
    bordered === "light"
      ? "border-lennar-image-preview-border border"
      : // `primary.main`, which is the *current* variant tile's border and the
        // whole of how the strip marks which sibling is on screen
        // (`item-variants-tiles.tsx:49-51`). A third colour rather than a
        // modifier on top, because a second `border-*` class would leave the
        // cascade to decide which wins.
        bordered === "primary"
        ? "border-lennar-blue-600 border"
        : bordered && "border-lennar-image-box-border border",
    className,
  );
}

/** One `<source>` for the `<picture>`, already grouped by format. */
export interface ItemImageBoxSource {
  type: string;
  srcSet: string;
}

interface ItemImageBoxBaseProps {
  /**
   * The image to draw. Absent or empty takes the donor's no-URL arm, which is
   * the words "Missing Image" and not a substituted asset — the table's empty
   * cell reaches it by handing the box an empty string
   * (`items-data-table.tsx:120`).
   */
  src?: string | null;
  alt: string;
  /**
   * The rendition ladder, as `<source>` elements inside the `<picture>`.
   *
   * The one place this box goes beyond the donor, and a ruled divergence rather
   * than an addition: renditions and `<picture>`/`srcset` replace the donor's
   * resize-CDN query parameter (spec #269, Further Notes). Empty before the
   * renditions worker has run, where `src` is the whole answer.
   */
  sources?: readonly ItemImageBoxSource[];
  /** The donor's `size`, default `small` as there. */
  size?: ItemImageBoxSize;
  /**
   * A pixel box, in place of a rung.
   *
   * `ImagePreview.tsx:58` is why: the details gallery's thumbnail cell is 68px
   * square, and 68 is not in `IMAGE_SIZE_MAP`. `ItemEmptyImageBox`'s `heightPx`
   * below is the same escape hatch for the same reason — a donor call site that
   * states its own geometry rather than picking a rung. `size` is ignored when
   * this is given, and `sizes` is stated from *this* number, so the `w`
   * descriptors resolve against the slot the browser will actually paint.
   *
   * A box rather than a width and a height: every donor call site that skips the
   * rung map asks for a square (`:58`), and two independent axes would let a
   * caller state a box whose `sizes` no longer describes it.
   */
  boxPx?: number;
  /**
   * The donor's `borderWidth`, as the boolean its call sites actually use.
   *
   * Defaulted *false* here where the donor defaults to 1: the card and the row
   * both pass `borderWidth={0}` and only the table takes the default, so the
   * donor's default is the odd one out rather than the common case.
   *
   * `"primary"` is the variant strip's current tile, which keeps the donor's
   * border *width* and changes only its colour.
   */
  bordered?: boolean | "primary";
  /**
   * The browser's fetch-priority hint.
   *
   * Defaulted `lazy` because that is what #302 ruled for the three catalog list
   * views, and its reasoning is explicitly scoped to them: "Every caller today
   * is one of the three catalog list views — a page of cards, which is the case
   * the hint exists for". The details gallery is not that case — it is one
   * above-the-fold picture and a strip beside it — and the donor's gallery sets
   * no hint at all (`ImagePreview.tsx:31-45,59-66`). So the ruling keeps its
   * default and the gallery opts out, rather than the ruling being reversed.
   */
  loading?: "lazy" | "eager";
  /**
   * Draw the generic "no picture" mark in place of a picture — the arm a slot
   * whose fetch **failed** takes.
   *
   * A named arm rather than a caller passing `ITEM_PLACEHOLDER_IMAGE` as `src`:
   * the mark has to be cropped to the donor's proportion, not merely fitted
   * into the box, and a call site spelling that crop itself is exactly the
   * drift the size map above exists to stop.
   *
   * This arm draws no `<img>`, so `src`, `sources` and `onError` are all
   * ignored when it is set — there is nothing left that can fetch, and so
   * nothing that can fail a second time. It is checked ahead of the no-URL arm:
   * a slot whose picture *failed* is not a slot that never had one, and the
   * donor draws those two differently. Why the crop: `ItemPlaceholderMark`.
   */
  placeholder?: boolean;
  onError?: () => void;
  className?: string;
}

/**
 * `heightPx` and `slotSizes` travel together, enforced by the type.
 *
 * A stated-height box is the one arm that cannot state its own `sizes` — the
 * other two *set* a width, so the box reads the number back off itself. Only
 * the caller knows how wide the column is, and a caller that forgets used to
 * get `100vw` silently: a ~340px slot pulling `w2048`, which is #499's own
 * defect wearing a different mask. The prop doc claimed "required whenever
 * `heightPx` is passed" while the type said optional; this is that claim moved
 * somewhere it holds.
 */
export type ItemImageBoxSlot =
  | { heightPx?: undefined; slotSizes?: undefined }
  | {
      /**
       * Full column width at this pixel height, in place of a square rung — the
       * matching half of `ItemEmptyImageBox`'s own explicit-geometry call, so a
       * caller that has one shape for the picture and one for the empty state does
       * not have to state them two different ways.
       *
       * **The third of three ways to say how big, and the only one that does not
       * fix a width.** `boxPx` above is a square, this is a stated height across
       * whatever column the box sits in. They are alternatives, never combined —
       * a caller passing both would get a square, since `boxPx` is read first.
       *
       * The Add Variant dialog's preview card is what needs it: the donor draws its
       * hero as `CardMedia component='img' height={250}` across the card's width
       * and its empty arm as `ItemEmptyImageBox style={{ height: 250 }}`
       * (`AddVariantIsland/components/item-details.tsx:151-166`) — one height, two
       * boxes. `size` is ignored when this is given, for the same reason it is
       * ignored on the empty box: the two are different ways of saying how big, and
       * honouring both would be a box whose width and height came from different
       * places.
       */
      /**
       * What the **stated-height** box tells the browser its width is — a `sizes`
       * value, required by the arm it sits on rather than merely documented as
       * required.
       *
       * The other two ways of saying how big also *set* a width (a rung's `w-*`, a
       * square's pixels), so the box states its own `sizes` from that number. A
       * stated-height box deliberately fixes no width: it is as wide as the column
       * it was dropped into, and only the caller knows how wide that is. So the
       * caller says, the way `item-image-preview.tsx` does for its own slot.
       *
       * This used to be `100%`, which is **invalid** in a `sizes` list —
       * percentages are not a `<source-size-value>`, so the whole attribute failed
       * to parse and the browser fell back to `100vw`, pulling the ladder's widest
       * rung into a ~340px slot (#499). A `vw`-relative `calc` is the way to say
       * "as wide as my column"; a percentage is not.
       */
      heightPx: number;
      slotSizes: string;
    };

type ItemImageBoxProps = ItemImageBoxBaseProps & ItemImageBoxSlot;

export function ItemImageBox({
  src,
  alt,
  sources,
  size = "small",
  boxPx,
  heightPx,
  slotSizes: statedSizes,
  bordered = false,
  loading = "lazy",
  placeholder = false,
  onError,
  className,
}: ItemImageBoxProps) {
  // The three ways to say how big, in the order they win. A square (`boxPx`)
  // and a stated height across the column (`heightPx`) each replace the rung, so
  // the rung's `h-*`/`w-*` must not also be in play — the same reasoning
  // `ItemEmptyImageBox` states below.
  const square = boxPx !== undefined;
  const tall = !square && heightPx !== undefined;
  const box = cn(
    boxShape(
      square ? undefined : tall ? "w-full" : ITEM_IMAGE_BOX_SIZES[size].className,
      bordered,
      className,
    ),
    // The rung and the square are both the donor's `ItemImageBox`, so they keep
    // its inline-level flow. The **stated-height** path is not that component at
    // all: the donor draws that hero as `CardMedia component='img'
    // height={250}` (`AddVariantIsland/components/item-details.tsx:154-162`),
    // whose img root is `display: block; width: 100%` with no padding and no
    // radius. Three visible differences follow from getting it wrong — a 1px
    // inset shrinking a stated 250px to 248px, clipped corners on the picture,
    // and an inline-level strut leaving a descender gap above the card's
    // divider rule. `cn` is tailwind-merge, so the later class in each group
    // wins over `boxShape`'s.
    tall ? "flex rounded-none" : "inline-flex",
    // `boxStyles`' 1px (`item-image-box.tsx:35-46`) belongs to the *rung* box,
    // and neither explicit call inherits it. The donor's 68px gallery cell is a
    // bare `Box` holding a `CardMedia` that fills it (`ImagePreview.tsx:58-66`),
    // so its picture area is the whole 66px inside the hairline; padding here
    // would draw it at 64 and put every thumbnail in the strip 2px off.
    !square && !tall && "p-px",
  );
  const explicitStyle = square
    ? { width: `${boxPx}px`, height: `${boxPx}px` }
    : tall
      ? { height: `${heightPx}px` }
      : undefined;
  // A stated-height box is as wide as its column rather than a known number of
  // pixels, so the caller states it (`slotSizes`) — and `ItemImageBoxSlot`
  // makes that a type error to forget rather than a silent default.
  //
  // The `100vw` arm is what an *untyped* caller still gets: the browser's own
  // default rather than a guess of ours, and deliberately not `100%`, which is
  // invalid in `sizes` and is the defect #499 fixed here. It is unreachable
  // from TypeScript, which is the point — it used to be reachable, and the
  // reachable version is what pulled `w2048` into a ~340px slot.
  const slotSizes = tall
    ? (statedSizes ?? "100vw")
    : `${square ? boxPx : ITEM_IMAGE_BOX_SIZES[size].px}px`;

  // Ahead of the no-URL arm: a slot whose picture failed is not the same thing
  // as a slot that never had one, and the donor draws them differently — words
  // for the second, and (via its image host) a full-bleed mark for the first.
  if (placeholder) {
    return (
      <div data-testid="item-image-box-placeholder" style={explicitStyle} className={box}>
        <ItemPlaceholderMark className="h-full w-full" label={alt} />
      </div>
    );
  }

  if (src === undefined || src === null || src === "") {
    return (
      <div
        data-testid="item-image-box-missing"
        style={explicitStyle}
        className={cn(
          box,
          // `body2` in `text.secondary`, wrapping — and **flush left**: the
          // donor's box sets `alignItems`/`alignContent` and never
          // `justifyContent`, and its `Typography` sets no `textAlign`
          // (`item-image-box.tsx:43-45,56-60`), so the two wrapped lines sit
          // against the left edge. `body2`'s `lineHeight: 1.43` is what bare
          // `text-sm` already gives (1.25rem/0.875rem); a `leading-*` here
          // would be drift.
          "text-lennar-image-box-text text-sm whitespace-normal",
        )}
      >
        Missing Image
      </div>
    );
  }

  return (
    <div data-testid="item-image-box" style={explicitStyle} className={box}>
      <ItemPicture
        sources={{ src, sources: sources ?? [] }}
        alt={alt}
        // The slot's own width, so the `w` descriptors resolve against the box
        // and not against the viewport: without it the browser assumes `100vw`
        // and pulls the widest rung into a 128px square.
        sizes={slotSizes}
        // A plain block, not a flex row. The `<img>` fills it and contains
        // itself, so there is nothing to centre — and a *flex* `<picture>`
        // hands the box a different baseline from the donor's bare `<img>`,
        // which closed up the line gap under every inline box this draws (the
        // card hero sat 5px high; owner D3, PR #662).
        pictureClassName="block h-full w-full"
        // The donor's inline style, transcribed: fill the box, and contain
        // rather than cover, so the whole image is always visible.
        className="block h-full w-full object-contain"
        // A ruled divergence from the donor, which sets no hint at all — see
        // the `loading` prop above.
        loading={loading}
        onError={onError}
      />
    </div>
  );
}

interface ItemEmptyImageBoxProps {
  /**
   * Reads "Missing Image" on a tinted panel instead of drawing the house.
   *
   * `ItemEmptyImageBox.tsx:35-52`. The donor's own callers pass plain
   * `!isApproved` (`ItemSearchIsland/.../ItemCard.tsx:247,275`) — the
   * `!isApproved && !isNotRecommended` chain belongs to the status *chip*, not
   * to this box.
   */
  isPendingApproval?: boolean;
  /** The alt the illustration carries. The donor passes the item's own. */
  alt: string;
  size?: ItemImageBoxSize;
  /**
   * The donor's **explicit-geometry** call: full column width at this pixel
   * height, in place of a square rung.
   *
   * `ImagePreview.tsx:47-50` reaches for the box exactly this way on the
   * details page — `style={{ height: 350, border: 'solid 1px lightgrey' }}` —
   * so it is a shape this component offers rather than something a page bolts
   * on with classes. `size` is ignored when it is given; the two are different
   * ways of saying how big, and honouring both would be a box whose width and
   * height came from different places.
   *
   * It also turns the scaling off. The 48px padding and 36px type below are the
   * donor's absolute values, and the ratio exists only so they fit a 128px
   * rung — applying it to a 350px box would print the words at 50px.
   */
  heightPx?: number;
  /**
   * A pixel **square**, in place of a rung — the sibling of `ItemImageBox`'s
   * `boxPx` above, and reached the same way: a donor call site whose slot is not
   * one of the five rungs. The gallery's 68px thumbnail cell is the case
   * (`ImagePreview.tsx:58`), for a thumbnail whose fetch failed.
   *
   * Unlike `heightPx` this keeps the scaling on: it stands in for a rung, so the
   * 48px padding and 36px type scale by *this* number over 250 exactly as a
   * rung's would. `size` is ignored when it is given.
   */
  boxPx?: number;
  /**
   * Draw the box's border.
   *
   * `"light"` is the details page's `border: 'solid 1px lightgrey'`
   * (`ImagePreview.tsx:49`) — the CSS keyword, #d3d3d3. `true` is
   * `ItemImageBox`'s own `grey.300`. The two donor callers disagree, so the
   * choice is the caller's rather than one grey standing in for both.
   */
  bordered?: boolean | "light";
  className?: string;
}

export function ItemEmptyImageBox({
  isPendingApproval = false,
  alt,
  size = "small",
  heightPx,
  boxPx,
  bordered = false,
  className,
}: ItemEmptyImageBoxProps) {
  // Three ways to say how big, and they do different things: the donor's
  // full-width panel (`heightPx`), an explicit square standing in for a rung
  // (`boxPx`), or a rung. Only the panel turns the scaling off.
  const explicit = heightPx !== undefined;
  const explicitBox = boxPx !== undefined;
  // `display: 'flex'` and `padding: 6` — this box's own, and not
  // `ItemImageBox`'s `inline-flex`/1px (`ItemEmptyImageBox.tsx:19-20`).
  //
  // **48px against a 250px box, carried over as a ratio.** The donor draws this
  // component at `height: 250` and full card width; the catalog's slot is the
  // donor's own 128px for this position, and 48px of padding either side does
  // not fit in it at all. So the padding scales as the donor's proportion —
  // 48 x (128/250) ~= 24px — the same scaling its font size takes below. That
  // padding is what makes "Missing Image" wrap to two lines here as it does
  // there. Raised as a decision-round entry on #302 rather than approximated
  // silently.
  const ratio = explicit ? 1 : (explicitBox ? boxPx : ITEM_IMAGE_BOX_SIZES[size].px) / 250;
  const scaled = Math.round(48 * ratio);
  // The explicit call replaces the rung's fixed square with the column's width
  // and its own height, so the rung's `h-*`/`w-*` must not also be in play.
  //
  // `rounded-none` on an unbordered stated-height box. The two donor callers of
  // this component's explicit geometry disagree about the corner, and the
  // disagreement travels with the border: the details page passes `border:
  // 'solid 1px lightgrey', borderRadius: 1` (`ImagePreview.tsx:47-50`), while
  // the Add Variant dialog's placeholder hero passes `style={{ height: 250,
  // width: 'auto' }}` and nothing else (`item-details.tsx:165-168`) — square,
  // on the largest single element in the dialog's left column. The gallery's
  // 68px square (`explicitBox`) is not that caller and keeps its radius.
  const box = cn(
    boxShape(
      explicit ? "w-full" : explicitBox ? undefined : ITEM_IMAGE_BOX_SIZES[size].className,
      bordered,
      className,
    ),
    "flex justify-center",
    explicit && !bordered && "rounded-none",
  );
  const explicitStyle = explicit
    ? { height: `${heightPx}px` }
    : explicitBox
      ? { width: `${boxPx}px`, height: `${boxPx}px` }
      : undefined;

  if (!isPendingApproval) {
    // The **explicit** call is the donor's `ItemEmptyImageBox` proper
    // (`ItemEmptyImageBox.tsx:16-51`): the tinted panel, 48px of padding, and
    // an illustration fixed at 148px tall across the full inner width. The
    // rung call is a different donor arm — the card and row substitute the
    // illustration as `ItemImageBox`'s own `imgUrl` (`item-card.tsx:202`), a
    // borderless, untinted, `object-contain` box — so the two arms stay apart.
    if (explicit) {
      return (
        <div
          data-testid="item-image-box-illustration"
          className={cn(box, "bg-lennar-image-placeholder")}
          style={{ ...explicitStyle, padding: `${scaled}px` }}
        >
          <img src={ITEM_PLACEHOLDER_ILLUSTRATION} alt={alt} className="block h-[148px] w-full" />
        </div>
      );
    }

    return (
      <div
        data-testid="item-image-box-illustration"
        className={cn(box, "p-px")}
        style={explicitStyle}
      >
        <img
          src={ITEM_PLACEHOLDER_ILLUSTRATION}
          alt={alt}
          className="block h-full w-full object-contain"
        />
      </div>
    );
  }

  return (
    // **An `<h2>`, because the donor's words are a `Typography variant='h2'`**
    // (`ItemEmptyImageBox.tsx:36`), which renders one. Tailwind's preflight
    // resets a heading's margin, size and weight to inherit, so this is the same
    // pixels as the `<div>` it replaces and a document outline that matches
    // modsy's.
    <h2
      data-testid="item-image-box-pending"
      className={cn(
        box,
        // `backgroundColor: 'backgroundLight.medium'` in the donor theme, black
        // text, centred — and centred here where `ItemImageBox` is not, because
        // this component sets `textAlign: 'center'` on its box and
        // `justifyContent: 'center'` on its text
        // (`ItemEmptyImageBox.tsx:25,42-46`).
        //
        // **Weight 400 and the h2 metrics, not the wrapper's 500 and 120%.** The
        // donor's inner wrapper does set `fontWeight: 500` and `lineHeight: 120%`
        // (`ItemEmptyImageBox.tsx:30-34`), but the words are a `Typography
        // variant='h2'` (`:36-49`) and the theme's `h2` stamps weight 400, line
        // height 1.25 and -0.5px tracking directly on that element
        // (`theme.ts:138-143`) — which beats anything inherited from the wrapper.
        // Reading the wrapper and stopping there is the mistake #303's source
        // sweep caught; the rendered string is the spec.
        "bg-lennar-image-placeholder text-center leading-[1.25] font-normal tracking-[-0.5px] text-black",
      )}
      // **The donor's `fontSize: 36`, scaled by the same 250->128 ratio.** 36px
      // does not fit a 128px box, and the size a reader perceives is relative to
      // the box it sits in, so the ratio is what carries over rather than the
      // absolute value. Inline because it is derived from the rung.
      style={{
        ...explicitStyle,
        padding: `${scaled}px`,
        fontSize: `${Math.round(36 * ratio)}px`,
      }}
    >
      Missing Image
    </h2>
  );
}
